/**
 * Org-level app enablement, driven by the admin "Per-org app access" screen.
 *
 * `GET /api/v1.0/config/apps/` (people service) returns the org-merged app
 * catalog and OMITS apps a super admin disabled for the org (TenantAppConfig)
 * or hid via NavigationOverride. An app is therefore "disabled for my org"
 * exactly when its app_id is absent from the payload.
 *
 * Fail-open contract (mirrors lib/moduleAccess.ts): unknown enablement
 * (no token / fetch error / malformed payload) or an unmapped route id must
 * never hide anything. Super-admin exemption is applied by callers via
 * readModuleAccess().isSuperAdmin.
 */

export const SUITE_CONFIG_APPS_URL = "/api/v1.0/config/apps/";

/** Sidebar route id -> AppDefinition.app_id, for org-toggleable apps only.
 *  Routes absent from this map (settings, people) are never gated by app
 *  access. `contacts` is pre-wired for a future standalone Contacts module. */
/**
 * Route id → the app(s) that gate it. A list means "any of": the route is
 * reachable while at least one of those apps is enabled.
 */
export const ROUTE_APP_IDS: Record<string, string | readonly string[]> = {
  dashboard: "assistant", // Intelligence Hub — gateable via the `assistant` app (people migration 0056)
  planner: "calendar",
  email: "email",
  call: "video",
  chat: "chat",
  vault: "drive",
  write: "docs",
  present: "present",
  grid: "grid",
  // Files hosts the Write / Present / Grid editors, so it stays reachable
  // while any one of them is enabled.
  files: ["drive", "docs", "present", "grid"],
  tasks: "tasks",
  marketplace: "marketplace",
  contacts: "contacts",
  // Paired with the AppDefinition row seeded in people migration 0050: a mapped
  // route with no catalog row would be blocked for everybody, so the two land
  // together.
  homework: "homework",
};

export async function fetchEnabledAppIds(
  getAccessToken: () => Promise<string | null>,
): Promise<Set<string> | null> {
  try {
    const token = await getAccessToken();
    if (!token) return null;
    const res = await fetch(SUITE_CONFIG_APPS_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { apps?: Array<{ id?: unknown }> };
    if (!Array.isArray(data.apps)) return null;
    return new Set(
      data.apps
        .map((row) => row.id)
        .filter((id): id is string => typeof id === "string"),
    );
  } catch {
    return null;
  }
}

export function isAppEnabled(
  enabledIds: Set<string> | null,
  routeId: string,
): boolean {
  if (!enabledIds) return true; // unknown -> fail open
  const appIds = ROUTE_APP_IDS[routeId];
  if (!appIds) return true; // route not org-toggleable
  return typeof appIds === "string" ? enabledIds.has(appIds) : appIds.some((id) => enabledIds.has(id));
}
